import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode
} from 'react'

export type AppMenuSource = 'profile' | 'more' | 'avatar'

type OpenOptions = {
  source?: AppMenuSource
}

type AppMenuContextValue = {
  isOpen: boolean
  source: AppMenuSource | null
  open: (options?: OpenOptions) => void
  close: () => void
  toggle: (options?: OpenOptions) => void
  onRequestEditAdditionalInfo: (() => void) | null
  setOnRequestEditAdditionalInfo: (handler: (() => void) | null) => void
}

const AppMenuContext = createContext<AppMenuContextValue | null>(null)

export function AppMenuProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [source, setSource] = useState<AppMenuSource | null>(null)
  const [onRequestEditAdditionalInfo, setOnRequestEditAdditionalInfo] = useState<
    (() => void) | null
  >(null)

  const close = useCallback(() => {
    setIsOpen(false)
    setSource(null)
  }, [])

  const open = useCallback((options?: OpenOptions) => {
    setSource(options?.source ?? null)
    setIsOpen(true)
  }, [])

  const toggle = useCallback(
    (options?: OpenOptions) => {
      if (isOpen) {
        close()
        return
      }

      open(options)
    },
    [close, isOpen, open]
  )

  const value = useMemo(
    () => ({
      isOpen,
      source,
      open,
      close,
      toggle,
      onRequestEditAdditionalInfo,
      setOnRequestEditAdditionalInfo
    }),
    [
      close,
      isOpen,
      onRequestEditAdditionalInfo,
      open,
      source,
      toggle
    ]
  )

  return (
    <AppMenuContext.Provider value={value}>{children}</AppMenuContext.Provider>
  )
}

export function useAppMenu() {
  const context = useContext(AppMenuContext)

  if (!context) {
    throw new Error('useAppMenu must be used within AppMenuProvider')
  }

  return context
}
